export type TimePeriod = 'night' | 'dawn' | 'midday' | 'afternoon' | 'sunset';

export interface CloudMascotProps {
  size?: number;
  className?: string;
}

export interface TimeOfDayConfig {
  period: TimePeriod;
  label: string;
  /** CSS gradient string for the header background */
  headerGradient: string;
  /** "Play Moments" text color in the header */
  headerTextColor: string;
  /** Which decoration variant to render in the header */
  headerDecoration: 'stars-glow' | 'stars-streaks' | 'sun-small' | 'sun-large' | 'sunset-streaks';
  /** Solid header color — used by non-home pages (library, category, profile) for their top bar */
  headerBackground: string;
  /** BG gradient layer — start color */
  bgGradientStart: string;
  /** BG gradient layer — via color (if applicable, else same as start) */
  bgGradientVia: string | null;
  /** BG gradient layer — via position (percentage) */
  bgGradientViaPosition: string | null;
  /** BG gradient layer — end color */
  bgGradientEnd: string;
  /** BG gradient layer — end position (percentage, default 100%) */
  bgGradientEndPosition: string;
  /** Filter container background overlay */
  filterBackground: string;
  /** Whether night decorations (stars, Zs, glow) should show */
  showNightDecorations: boolean;
  /** Whether morning glow (warm sunrise radial gradients) should show */
  showMorningGlow: boolean;
}

export const TIME_OF_DAY_CONFIGS: Record<TimePeriod, TimeOfDayConfig> = {
  night: {
    period: 'night',
    label: 'Night',
    headerGradient: 'linear-gradient(260.75deg, #2C1D4E 0%, #1B2831 100%)',
    headerTextColor: '#58DA8E',
    headerDecoration: 'stars-glow',
    headerBackground: '#4646FF',
    bgGradientStart: '#4646FF',
    bgGradientVia: null,
    bgGradientViaPosition: null,
    bgGradientEnd: 'rgba(70, 70, 255, 0)',
    bgGradientEndPosition: '82.212%',
    filterBackground: 'rgba(255, 255, 255, 0.3)',
    showNightDecorations: true,
    showMorningGlow: false,
  },
  dawn: {
    period: 'dawn',
    label: 'Dawn',
    headerGradient: 'linear-gradient(259.80deg, #EBB495 12.78%, #6365CB 15.68%, #1B2831 100%)',
    headerTextColor: '#CAA2FF',
    headerDecoration: 'stars-streaks',
    headerBackground: '#005880',
    bgGradientStart: '#005880',
    bgGradientVia: '#0078AF',
    bgGradientViaPosition: '21.154%',
    bgGradientEnd: 'rgba(255, 240, 157, 0)',
    bgGradientEndPosition: '100%',
    filterBackground: 'rgba(255, 255, 255, 0.2)',
    showNightDecorations: false,
    showMorningGlow: true,
  },
  midday: {
    period: 'midday',
    label: 'Midday',
    headerGradient: 'linear-gradient(260.75deg, #37ABE0 0%, #00357F 50.96%, #1B2831 100%)',
    headerTextColor: '#57CAFF',
    headerDecoration: 'sun-small',
    headerBackground: '#0080D0',
    bgGradientStart: '#0080D0',
    bgGradientVia: '#01A7C8',
    bgGradientViaPosition: '22.5%',
    bgGradientEnd: 'rgba(1, 167, 200, 0)',
    bgGradientEndPosition: '100%',
    filterBackground: 'rgba(255, 255, 255, 0.3)',
    showNightDecorations: false,
    showMorningGlow: false,
  },
  afternoon: {
    period: 'afternoon',
    label: 'Afternoon',
    headerGradient: 'linear-gradient(-87.00deg, #256CC9 0.32%, #00306B 50.51%, #1B2831 99.75%)',
    headerTextColor: '#4696FF',
    headerDecoration: 'sun-large',
    headerBackground: '#0080D0',
    bgGradientStart: '#0080D0',
    bgGradientVia: '#01A7C8',
    bgGradientViaPosition: '22.5%',
    bgGradientEnd: 'rgba(1, 167, 200, 0)',
    bgGradientEndPosition: '100%',
    filterBackground: 'rgba(255, 255, 255, 0.3)',
    showNightDecorations: false,
    showMorningGlow: false,
  },
  sunset: {
    period: 'sunset',
    label: 'Sunset',
    headerGradient: 'linear-gradient(-81.13deg, #92436B 4.20%, #3C1885 29.37%, #1B2032 100%)',
    headerTextColor: '#EF8A57',
    headerDecoration: 'sunset-streaks',
    headerBackground: '#3537D6',
    bgGradientStart: '#3537D6',
    bgGradientVia: null,
    bgGradientViaPosition: null,
    bgGradientEnd: 'rgba(62, 0, 128, 0)',
    bgGradientEndPosition: '100%',
    filterBackground: 'rgba(255, 255, 255, 0.3)',
    showNightDecorations: false,
    showMorningGlow: false,
  },
};

/**
 * The main wrapper gradient is consistent across all periods.
 * This is the gradient that goes from cyan to white below the BG gradient layer.
 */
export const WRAPPER_GRADIENT = {
  from: '#00A7C8',
  fromPosition: '21.137%',
  to: '#FFFFFF',
  toPosition: '92.114%',
};

/**
 * Determine the time period based on the current hour (0-23).
 * Boundaries:
 * - Dawn:      6 AM – 10 AM  (hours 6-9)
 * - Midday:    10 AM – 2 PM  (hours 10-13)
 * - Afternoon: 2 PM – 4 PM   (hours 14-15)
 * - Sunset:    4 PM – 8 PM   (hours 16-19)
 * - Night:     8 PM – 6 AM   (hours 20-5)
 */
export function getTimePeriod(hour: number): TimePeriod {
  if (hour >= 6 && hour < 10) return 'dawn';
  if (hour >= 10 && hour < 14) return 'midday';
  if (hour >= 14 && hour < 16) return 'afternoon';
  if (hour >= 16 && hour < 20) return 'sunset';
  return 'night';
}
